import * as React from "react";
import { Button } from "@/components/ui/button";
import { Sparkles, Lock } from "lucide-react";
import { Modal } from "@/components/vb";
import { useAuth } from "@/context/AuthContext";
import { projectsInstance } from "@/services/ProjectsService";
import { useReportsStore } from "@/store/store";
import { useWorkspace } from "@/context/WorkspaceContext";
import { renderMarkdownSafe } from "@/lib/markdown";

type Props = {
    report: any;
    /** The signed-in user is on the free plan (drives the blurred upsell). */
    isFreeUser?: boolean;
    /** Open the billing tab (free-plan unlock). */
    onUnlock?: () => void;
};

/** Flatten markdown to plain text. */
function stripMd(md: string): string {
    return (md || "")
        .replace(/```[\s\S]*?```/g, " ") // code fences
        .replace(/`([^`]+)`/g, "$1") // inline code
        .replace(/!\[[^\]]*\]\([^)]*\)/g, " ") // images
        .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1") // links -> text
        .replace(/^\s{0,3}#{1,6}\s+/gm, "") // headings
        .replace(/^\s{0,3}[-*+]\s+/gm, "") // bullets
        .replace(/^\s{0,3}>\s?/gm, "") // blockquotes
        .replace(/[*_]{1,3}/g, "") // emphasis
        .replace(/\s+/g, " ")
        .trim();
}

/** A top-level lede from the (already-stripped) summary — the first couple of
 *  sentences, cut on a sentence boundary — so it reads as a compact takeaway
 *  sized to its content, not a long blob clamped behind a fade. The backend now
 *  generates a concise ~55-word summary (see MarcusAIAgent), which fits under
 *  `max` and renders whole; only long legacy summaries get truncated, with the
 *  full formatted version one click away in the dialog. */
function ledeFrom(plain: string, max = 380): string {
    if (plain.length <= max) return plain;
    const slice = plain.slice(0, max);
    const stop = Math.max(slice.lastIndexOf(". "), slice.lastIndexOf("! "), slice.lastIndexOf("? "));
    if (stop > max * 0.5) return slice.slice(0, stop + 1);
    return slice.replace(/\s+\S*$/, "") + "…";
}

/** A small "Marcus AI" kicker (brand sparkles), matching the mock's indigo
 *  Marcus treatment. */
function MarcusLabel() {
    return (
        <div className="inline-flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-[0.14em] text-vb-brand-pressed">
            <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
            Marcus AI
        </div>
    );
}

/** The report's executive summary, folded compact into the score card's Score
 *  tab (AITS-491). Replaces the standalone gradient MarcusDialog card: a top-level
 *  Marcus kicker, the concise `executive_summary_short` takeaway (falling back to
 *  a client-side lede of the full summary for legacy reports) with the full
 *  comprehensive summary behind "Read full summary", and the same gating states
 *  (free-plan upsell, upgrade-to-full, pre-subscription unavailable) on v2 tokens. */
export function MarcusSummary({ report, isFreeUser = false, onUnlock }: Props) {
    const [open, setOpen] = React.useState(false);
    const { token } = useAuth();
    const setGenerateDialogRequest = useReportsStore((s) => s.setGenerateDialogRequest);
    const { activeWorkspace } = useWorkspace();

    const isPaid = !!report?.is_paid;
    const canBeUpgraded = !!report?.can_be_upgraded;
    const reportId = report?.id;
    // Full (comprehensive) summary — also what the emailed/PDF report shows.
    const summary: string = report?.executive_summary ?? "";
    // Backend-generated concise takeaway for the dashboard (AITS-491). Preferred
    // when present; legacy reports without it fall back to a client-side lede.
    const shortSummary: string = (report?.executive_summary_short ?? "").trim();

    const handleUpgrade = async () => {
        if (!reportId) return;
        const res = await projectsInstance.upgradeReport(reportId, token, activeWorkspace?.id);
        const upgradedId = res?.report?.report_id;
        // Stay put; the progress dialog is shell-mounted and navigates to the
        // upgraded report itself once it completes. The old bounce through `/`
        // only existed so Home could mount the polling hook (AITS-540).
        if (upgradedId != null) setGenerateDialogRequest(upgradedId as number);
    };

    // ── Paid report with a summary → concise takeaway, full summary on demand ──
    if (isPaid && (shortSummary || summary)) {
        const plain = stripMd(summary);
        const lede = shortSummary || ledeFrom(plain);
        // Offer "Read full summary" when the full write-up has more than the lede
        // (always true for the backend short summary vs the comprehensive one).
        const isTruncated = plain.length > lede.length;
        return (
            <div className="flex flex-col gap-2 border-t border-vb-line pt-4">
                <MarcusLabel />
                <p className="text-sm leading-6 text-vb-text-2">{lede}</p>
                {isTruncated && (
                    <button
                        type="button"
                        onClick={() => setOpen(true)}
                        className="self-start text-sm font-semibold text-vb-brand hover:text-vb-brand-hover"
                    >
                        Read full summary
                    </button>
                )}

                {/* Full comprehensive summary — the shared v2 Modal + `.vb-md`
                    LLM-response styling (as in FullResponseModal), without the
                    model-source header. */}
                <Modal
                    open={open}
                    onOpenChange={setOpen}
                    title="Executive summary"
                    widthClassName="max-w-2xl"
                >
                    <div
                        className="vb-md"
                        dangerouslySetInnerHTML={{ __html: renderMarkdownSafe(summary) }}
                    />
                </Modal>
            </div>
        );
    }

    // ── Paid report, but no summary generated (edge) ──
    if (isPaid) {
        return (
            <div className="flex flex-col gap-2 border-t border-vb-line pt-4">
                <MarcusLabel />
                <p className="text-sm text-vb-text-3">No summary was generated for this report.</p>
            </div>
        );
    }

    // ── Free plan → upsell to unlock Marcus ──
    if (isFreeUser) {
        return (
            <div className="flex flex-col gap-3 border-t border-vb-line pt-4">
                <MarcusLabel />
                <p className="text-sm leading-6 text-vb-text-2">
                    Marcus AI builds your 90-day action plan and gives real-time advice, grounded in
                    Marcus Sheridan's <i>They Ask, You Answer</i>. Unlock it with your subscription.
                </p>
                <Button variant="gold" onClick={onUnlock} className="self-start">
                    <Lock className="mr-2 h-4 w-4" />
                    Subscribe to unlock
                </Button>
            </div>
        );
    }

    // ── Paid user viewing a not-yet-full report that can be upgraded ──
    if (canBeUpgraded) {
        return (
            <div className="flex flex-col gap-3 border-t border-vb-line pt-4">
                <MarcusLabel />
                <p className="text-sm leading-6 text-vb-text-2">
                    Generate the full report to see Marcus's summary for this month.
                </p>
                <Button onClick={handleUpgrade} className="self-start">
                    Generate full report
                </Button>
            </div>
        );
    }

    // ── Paid user, report predates the subscription → unavailable ──
    return (
        <div className="flex flex-col gap-2 border-t border-vb-line pt-4">
            <MarcusLabel />
            <p className="text-sm text-vb-text-3">
                This report was created before your subscription was active, so Marcus's summary
                isn't available for it.
            </p>
        </div>
    );
}

export default MarcusSummary;
